import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  SimpleGrid,
  Button,
  Icon,
  Spinner,
  Alert,
  Table,
  Link,
} from '@chakra-ui/react'
import {
  FiUsers,
  FiActivity,
  FiDollarSign,
  FiTrendingUp,
  FiAlertCircle,
  FiRefreshCw,
} from 'react-icons/fi'
import { apiConfig } from '../../config/api'
import { StatsCard } from '../../components/admin/StatsCard'
import { StatusBadge } from '../../components/admin/StatusBadge'
import { useNavigate } from 'react-router-dom'

interface OverviewData {
  total_practitioners: number
  practitioners_by_step: Record<string, number>
  total_campaigns: number
  campaigns_by_status: Record<string, number>
  total_revenue_cents: number
  revenue_this_month_cents: number
  total_impressions_delivered: number
  avg_visibility_score: number
  meta_errors: {
    total_failed: number
    recent_errors: Array<{
      campaign_id: string
      practitioner_id: string
      practitioner_name: string
      error: string
      failed_at: string | null
      meta_status: string | null
    }>
  }
}

export const OverviewPage: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<OverviewData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(`${apiConfig.baseURL}/api/v1/admin/overview`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to load overview data')
      }

      const result = await response.json()
      setData(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load overview data')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">Loading overview...</Text>
        </VStack>
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Content>
          {error || 'Failed to load overview data'}
        </Alert.Content>
      </Alert.Root>
    )
  }

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  return (
    <Container maxW="7xl">
      <VStack gap={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Overview
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Platform-wide analytics and metrics
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchData}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Platform Errors Alert */}
        {data.meta_errors.total_failed > 0 && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <VStack align="start" gap={1} flex={1}>
                <Text fontWeight="bold">
                  {data.meta_errors.total_failed} ad platform campaign creation(s) failed
                </Text>
                <Text fontSize="sm">
                  Review and retry failed campaigns from the{' '}
                  <Link
                    color="blue.500"
                    onClick={() => navigate('/admin/campaigns?metaErrorOnly=true')}
                    cursor="pointer"
                    textDecoration="underline"
                  >
                    Campaigns page
                  </Link>
                </Text>
              </VStack>
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
          <StatsCard
            label="Total Practitioners"
            value={data.total_practitioners.toLocaleString()}
            icon={FiUsers}
            iconColor="blue.500"
          />
          <StatsCard
            label="Active Campaigns"
            value={data.campaigns_by_status.active || 0}
            subtitle={`${data.total_campaigns} total`}
            icon={FiActivity}
            iconColor="green.500"
          />
          <StatsCard
            label="Total Revenue"
            value={formatCurrency(data.total_revenue_cents)}
            subtitle={`${formatCurrency(data.revenue_this_month_cents)} this month`}
            icon={FiDollarSign}
            iconColor="purple.500"
          />
          <StatsCard
            label="Impressions Delivered"
            value={data.total_impressions_delivered.toLocaleString()}
            icon={FiTrendingUp}
            iconColor="orange.500"
          />
        </SimpleGrid>

        {/* Platform Errors Table */}
        {data.meta_errors.recent_errors.length > 0 && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <HStack justify="space-between">
                  <Heading size="md" color="gray.900">
                    Recent Platform Errors
                  </Heading>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/admin/campaigns?metaErrorOnly=true')}
                  >
                    View All
                  </Button>
                </HStack>
                <Box overflowX="auto">
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Campaign ID</Table.ColumnHeader>
                        <Table.ColumnHeader>Practitioner</Table.ColumnHeader>
                        <Table.ColumnHeader>Error</Table.ColumnHeader>
                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                        <Table.ColumnHeader>Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.meta_errors.recent_errors.map((error) => (
                        <Table.Row key={error.campaign_id}>
                          <Table.Cell>
                            <Link
                              color="blue.500"
                              onClick={() =>
                                navigate(`/admin/campaigns/${error.campaign_id}`)
                              }
                              cursor="pointer"
                            >
                              {error.campaign_id.substring(0, 8)}...
                            </Link>
                          </Table.Cell>
                          <Table.Cell>{error.practitioner_name || 'Unknown'}</Table.Cell>
                          <Table.Cell>
                            <Text
                              fontSize="xs"
                              maxW="300px"
                              isTruncated
                              title={error.error}
                            >
                              {error.error}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <StatusBadge
                              status={error.meta_status || 'failed'}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              size="xs"
                              onClick={() =>
                                navigate(`/admin/campaigns/${error.campaign_id}`)
                              }
                            >
                              View
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Campaigns by Status */}
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Campaigns by Status
                </Heading>
                <VStack align="stretch" gap={2}>
                  {Object.entries(data.campaigns_by_status).map(([status, count]) => (
                    <HStack key={status} justify="space-between">
                      <StatusBadge status={status} />
                      <Text fontWeight="bold">{count}</Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Practitioners by Step
                </Heading>
                <VStack align="stretch" gap={2}>
                  {Object.entries(data.practitioners_by_step)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([step, count]) => (
                      <HStack key={step} justify="space-between">
                        <Text>Step {step}</Text>
                        <Text fontWeight="bold">{count}</Text>
                      </HStack>
                    ))}
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>
      </VStack>
    </Container>
  )
}

